import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbIconModule, NbInputModule, NbSelectModule, NbTagModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { EmailComponentType } from '../../interfaces/email-component.interface'
import { EmailTheme } from '../../interfaces/email-theme.interface'
import { EmailComposerStateService } from '../../services/email-composer-state.service'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-component-manager',
  templateUrl: './component-manager.component.html',
  styleUrls: ['./component-manager.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NbCardModule, NbButtonModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbIconModule, NbTagModule]
})
export class ComponentManagerComponent implements OnInit, OnDestroy {
  @Output() editComponent = new EventEmitter<number>()
  @Output() createComponent = new EventEmitter<void>()

  components: EmailComponentType[] = []
  loading = false

  // Search and filters
  searchControl = new FormControl('')
  categoryFilterControl = new FormControl('')
  activeFilterControl = new FormControl(true)
  systemComponentControl = new FormControl('')
  selectedThemeControl = new FormControl('')

  // Available categories and themes
  categories: string[] = []
  availableThemes: EmailTheme[] = []
  selectedTheme: EmailTheme | null = null

  // Delete confirmation
  showDeleteConfirmation = false
  componentToDelete: EmailComponentType | null = null

  // MJML rendering cache
  private mjmlCache = new Map<string, SafeHtml>()
  // Track in-flight render requests to prevent duplicate API calls per key
  private inFlightRenders = new Set<string>()

  private destroy$ = new Subject<void>()
  private currentSearch = ''

  constructor(private _emailComposerService: EmailComposerService, private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef, private state: EmailComposerStateService) {}

  ngOnInit(): void {
    this.initFilters()
    this.loadThemes()
    this.loadComponents()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initFilters(): void {
    // Search filter uses shared header
    this.state.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.currentSearch = value || ''
      this.loadComponents()
    })

    // Category filter
    this.categoryFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadComponents())

    // Active filter
    this.activeFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadComponents())

    // System component filter
    this.systemComponentControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadComponents())

    // Theme filter - clear cache and reload when theme changes
    this.selectedThemeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((themeId) => {
      this.selectedTheme = this.availableThemes.find((t) => t.id?.toString() === themeId) || null
      this.mjmlCache.clear() // Clear cache to force re-render with new theme
      this.inFlightRenders.clear() // Reset in-flight markers
    })
  }

  loadComponents(): void {
    this.loading = true
    const search: string | undefined = this.currentSearch || undefined
    const category = this.categoryFilterControl.value || undefined
    const is_active = this.activeFilterControl.value
    const is_system_component = this.systemComponentControl.value === '' ? undefined : this.systemComponentControl.value === 'true'

    this._emailComposerService
      .getAvailableComponents(search, category, is_active, is_system_component)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.components = response.data
          this.updateCategories()
          this.loading = false
        },
        error: (error) => {
          console.error('Error loading components:', error)
          this.loading = false
        }
      })
  }

  private updateCategories(): void {
    this.categories = this.components.map((c) => c.category).sort()
  }

  private loadThemes(): void {
    this._emailComposerService
      .getThemes(undefined, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableThemes = response.data
          // Pick default theme or first available, and propagate selection
          const defaultTheme = this.availableThemes.find((t) => t.is_default) || this.availableThemes[0] || null
          if (defaultTheme) {
            this.selectedTheme = defaultTheme
            // Update the selector control without emitting extra events
            this.selectedThemeControl.setValue(defaultTheme.id?.toString() || '', { emitEvent: true })
            this._emailComposerService.setSelectedTheme(defaultTheme)
          }
        },
        error: (error) => {
          console.error('Error loading themes:', error)
        }
      })
  }

  onCreateComponent(): void {
    this.createComponent.emit()
  }

  // Card click handler - navigate to edit
  onCardClick(component: EmailComponentType): void {
    this.editComponent.emit(component.id!)
  }

  // Delete button click handler
  onDeleteClick(component: EmailComponentType, event: Event): void {
    event.stopPropagation() // Prevent card click
    this.componentToDelete = component
    this.showDeleteConfirmation = true
  }

  // Confirm delete
  confirmDelete(): void {
    if (this.componentToDelete) {
      this._emailComposerService
        .deleteComponent(this.componentToDelete.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadComponents()
            this.cancelDelete()
          },
          error: (error) => {
            console.error('Error deleting component:', error)
            this.cancelDelete()
          }
        })
    }
  }

  // Cancel delete
  cancelDelete(): void {
    this.showDeleteConfirmation = false
    this.componentToDelete = null
  }

  // Use precompiled HTML when available for list previews with caching to prevent repeated processing during scrolling
  getRenderedMjml(component: EmailComponentType): SafeHtml {
    // Create a cache key based on component ID
    const cacheKey = `${component.id}`

    // Return cached version if available
    if (this.mjmlCache.has(cacheKey)) {
      return this.mjmlCache.get(cacheKey)!
    }

    let renderedHtml: SafeHtml

    if (component.html_template) {
      renderedHtml = this.sanitizer.bypassSecurityTrustHtml(`
        <div style="overflow: hidden;">
          ${component.html_template}
        </div>
      `)
    } else {
      // Fallback: lightweight placeholder when no html_template is present
      renderedHtml = this.sanitizer.bypassSecurityTrustHtml(`
        <div style="display: flex; align-items: center; justify-content: center; height: 200px; background: #f8fafc; color: #6b7280;">
          <div>Preview unavailable</div>
        </div>
      `)
    }

    // Cache the result to prevent repeated processing during scrolling
    this.mjmlCache.set(cacheKey, renderedHtml)
    return renderedHtml
  }

  getCategoryLabel(category: string): string {
    return category.charAt(0).toUpperCase() + category.slice(1)
  }

  getComponentIcon(category: string): string {
    const iconPaths: Record<string, string> = {
      layout: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19Z',
      content: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
      media: 'M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z',
      navigation:
        'M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z'
    }
    return iconPaths[category] || 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z'
  }
}
